import { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { formatNumber, formatPercent } from "../../utilities/format";
import i18n from "../../i18n";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable } from "../../components/DataTable/TableHook";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { detectionSourceLabel } from "../../DTOs/Common/DetectionSource";
import { TripleShift } from "../../types/Shift";
import { useTimezone } from "../../components/Providers/TimezoneProvider";
import { movementTimeInZone } from "./movementEntry";

interface ExpandedRehandleGroupRowProps {
  groupKey: string;
  selectedDate: DateTime;
  selectedShift: TripleShift;
  isCrushing: boolean;
}

export const ExpandedRehandleGroupRow: React.FC<ExpandedRehandleGroupRowProps> = ({
  groupKey,
  selectedDate,
  selectedShift,
  isCrushing
}) => {
  const { t } = useTranslation("geology");
  const { timezone } = useTimezone();
  const getRows = useCallback(
    () => geologyAjax.getGeoRehandleGroupConstituents(selectedDate, selectedShift, isCrushing, groupKey),
    [selectedDate, selectedShift, isCrushing, groupKey]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        {
          name: "Time",
          label: i18n.t("fields.time"),
          editable: false,
          type: "string",
          value: entry.movementTimeUtc
            ? movementTimeInZone(entry.movementTimeUtc, timezone ?? "UTC").toFormat("HH:mm")
            : "-",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Hour",
          label: i18n.t("fields.hour"),
          editable: false,
          type: "integer",
          value: entry.hour ?? "-",
          tailwindClasses: "py-3 basis-[10%]",
        },
        {
          name: "Loads",
          label: i18n.t("fields.loads"),
          editable: false,
          type: "integer",
          value: entry.loads ?? 0,
          tailwindClasses: "py-3 basis-[10%]",
        },
        {
          name: "Tonnes",
          label: i18n.t("fields.tonnes"),
          editable: false,
          type: "string",
          value: entry.tonnes != null
            ? formatNumber(entry.tonnes, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "-",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Source",
          label: i18n.t("fields.source"),
          editable: false,
          type: "string",
          value: detectionSourceLabel(entry.detectionSource),
          tailwindClasses: "py-3 basis-[20%]",
        },
        {
          name: "Confidence",
          label: t("groups.confidence"),
          editable: false,
          type: "string",
          // Note: blank rather than 0% on a hand-entered row, which has no confidence to report.
          value: entry.confidence != null ? formatPercent(Math.round(entry.confidence * 100)) : "",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Checked",
          label: t("groups.checked"),
          editable: false,
          type: "string",
          value: entry.isVerified ? i18n.t("actions.yes") : i18n.t("actions.no"),
          tailwindClasses: "py-3 basis-[15%]",
        }
      ]
    }
  }, [timezone, t]);

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
  } = useDataTable<GeoRehandleDTO>({
    queryKey: ['geology', 'rehandleGroupConstituents', isCrushing, selectedDate.toISODate(), selectedShift, groupKey],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: 10,
  });

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      isNestedTable={true}
    />
  );
}
